import { WaypointType } from "./components/directions/waypoint.js";
import { HTTPService, HTTPServiceError, JsonResultClass } from "../core/util/httpManager.js";
import { Helper } from "../core/util/helper.js";
import { NotificationHelper } from "../core/util/notificationHelper.js";

export class GeomapService {
    private _translations: any = null;
    private _directionsService: google.maps.DirectionsService;
    private _geocoder: google.maps.Geocoder;

    constructor() {
        Helper.getTranslations(["WebSMS.Messages.GeomapServerNotAccessible", "GeoMap.NoApiKey"]).then(result => {
            this._translations = result.Data;
        });
    }

    getApiKey = () => {
        return new Promise<string>((resolve, reject) => {
            HTTPService.get<JsonResultClass<string>>('Directions/GetApiKey')
                .then(result => {
                    if (result.Data != null && result.Code === 0) {
                        resolve(result.Data);
                    }
                    else {
                        reject(result.Message);
                    }
                }, (result: HTTPServiceError) => {
                    reject(`${result.status}: ${result.statusText}`);
                });
        });
    }

    googleGeoCode = (address: string) => {
        const promise = new Promise<Array<google.maps.GeocoderResult>>((resolve, reject) => {
            if (this._geocoder == null) {
                this._geocoder = new google.maps.Geocoder();
            }
            this._geocoder.geocode({ address: address } as google.maps.GeocoderRequest, (result, status) => {
                if (status === google.maps.GeocoderStatus.OK) {
                    resolve(result);
                }
                else {
                    reject(status);
                }
            })
        });

        console.log("Geocode request");
        return promise;
    }

    googleRoute = (points: any[], optimize: boolean) => {
        let waypointsBetween = points.reduce((res, p, index) => {
            if (index !== 0 && index !== points.length - 1) {
                res.push({ location: { lat: p.lat, lng: p.lng }, stopover: true } as google.maps.DirectionsWaypoint);
            }
            return res;
        }, []);
        const promise = new Promise<google.maps.DirectionsResult>((resolve, reject) => {
            if (this._directionsService == null) {
                this._directionsService = new google.maps.DirectionsService();
            }
            this._directionsService.route({
                origin: { lat: points[0].lat, lng: points[0].lng } as google.maps.LatLngLiteral,
                destination: { lat: points[points.length - 1].lat, lng: points[points.length - 1].lng } as google.maps.LatLngLiteral,
                waypoints: waypointsBetween,
                optimizeWaypoints: optimize,
                travelMode: google.maps.TravelMode.DRIVING
            }, (response, status) => {
                if (status === google.maps.DirectionsStatus.OK) {
                    resolve(response);
                } else {
                    reject(status);
                }
            });
        });

        console.log("Route request");
        return promise;
    }


    getTotalDistanceAndTime = (directionsResult: google.maps.DirectionsResult) => {
        let totalDistance = 0;
        let totalDuration = 0;
        let legs = directionsResult.routes[0].legs;
        for (let i = 0; i < legs.length; ++i) {
            totalDistance += legs[i].distance.value;
            totalDuration += legs[i].duration.value;
        }
        return { distance: totalDistance, duration: totalDuration };
    }

    getRouteLegs = (directionsResult: google.maps.DirectionsResult) => {
        return directionsResult.routes[0].legs;
    }

    initializeGoogleMaps = (callback: Function) => {
        let geomapElement = document.getElementById("googleMapsScript");
        if (!geomapElement) {
            window["googleMapsCallback"] = callback;
            this.getApiKey().then((result) => {
                let addScript = document.createElement("script");
                let key = result;
                addScript.setAttribute("src", `https://maps.googleapis.com/maps/api/js?key=${key}&callback=googleMapsCallback`);
                addScript.setAttribute("async", "async");
                addScript.setAttribute("defer", "defer");
                addScript.setAttribute("id", "googleMapsScript");
                document.body.appendChild(addScript);
            }, () => {
                NotificationHelper.showErrorMessage(this._translations["GeoMap.NoApiKey"]);
            });
        }
        else {
            callback();
        }
    }

    getAddressById = (adrnr: string) => {
        return HTTPService.get("Address/Get", { id: adrnr });
    }

    getResourceById = (ressnr: string) => {
        return HTTPService.get("Resource/Init", { id: ressnr });
    }

    getResourcesByIds = (ressnrs: Array<string>) => {
        return HTTPService.get("Directions/GetResourcesByIds", { ressnrs: ressnrs });
    }

    getAssignment = (projnr: string, pindex: number) => {
        return HTTPService.get("ResourcePlanning/Get", { projNr: projnr, pIndex: pindex });
    }

    getAssignmentsByRessNrs = (ressnrs: Array<string>, start: Date, end: Date, groups: Array<string>) => {
        return HTTPService.get("Directions/GetAssignmentsByRessNrs", { ressnrs: ressnrs, start: start, end: end, groups: groups });
    }

    getAddressesByDistance = (latitude: number, longitude: number, distance: number, useMiles: boolean) => {
        return HTTPService.get("Directions/GetAddressesByDistance", { latitude: latitude, longitude: longitude, distance: distance, useMiles: useMiles });
    }

    getMachinesByDistance = (latitude: number, longitude: number, distance: number, useMiles: boolean) => {
        return HTTPService.get("Directions/GetMachinesByDistance", { latitude: latitude, longitude: longitude, distance: distance, useMiles: useMiles });
    }

    getResourcesByDistance = (latitude: number, longitude: number, distance: number, useMiles: boolean) => {
        return HTTPService.get("Directions/GetResourcesByDistance", { latitude: latitude, longitude: longitude, distance: distance, useMiles: useMiles });
    }

    getAssignmentsByDistance = (latitude: number, longitude: number, distance: number, assignmentStart: Date, assignmentEnd: Date, useMiles: boolean) => {
        return HTTPService.get("Directions/GetAssignmentsByDistance", { latitude: latitude, longitude: longitude, distance: distance, assignmentStart: assignmentStart, assignmentEnd: assignmentEnd, useMiles: useMiles });
    }

    getTicketsByDistance = (latitude: number, longitude: number, distance: number, useMiles: boolean) => {
        return HTTPService.get("Directions/GetTicketsByDistance", { latitude: latitude, longitude: longitude, distance: distance, useMiles: useMiles });
    }

    getPerimeterTabOrder = () => {
        return HTTPService.get("Directions/GetTabOrder");
    }

    saveGeoData = (latitude: number, longitude: number, entityType: WaypointType, id: string) => {
        return HTTPService.get("Directions/SaveGeoData", { latitude: latitude, longitude: longitude, entityType: entityType, id: id });
    }

    getConfig = () => {
        return HTTPService.get("MetaData/GetJson", { metaDataType: "geomapConfig" });
    }

    getContainerAssignments = (start: Date, end: Date) => {
        return HTTPService.get("Directions/GetContainerAssignments", { start: start ? start.toISOString() : null, end: end ? end.toISOString() : null });
    }

    getActiveResourceAssignments = (start: Date, end?: Date) => {
        return HTTPService.get("Directions/GetActiveResourceAssignments", { start: start.toISOString(), end: end ? end.toISOString() : null });
    }
}
